import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams, useNavigate, useLocation, useParams } from 'react-router-dom';
import { Plus, ScanSearch } from 'lucide-react';
import toast from '../design-system/toast';
import { ScanBatch, ScanListResponse, ScanStats } from '../types/scan';
import { ApplicationResponse, ApplicationListResponse, FromApplicationContext } from '../types/application';
import { useAuthenticatedApi } from '../hooks/useAuthenticatedApi';
import { apiEndpoints } from '../utils/api';
import { useOrganization } from '../contexts/OrganizationContext';
import { Spinner } from '../design-system';
import { SingleSelectDropdown } from '../components/common/SingleSelectDropdown';
import { useScanBranches } from '../hooks/useScanBranches';
import ScanBatchRow from '../components/scans/ScanBatchRow';
import ScanBatchRunningRow from '../components/scans/ScanBatchRunningRow';
import CreateScanModal from '../components/scans/CreateScanModal';

// Status filter chips. "Succeeded" lists finished scans; "Running" surfaces the
// in-progress (active) scans.
const STATUS_CHIPS: { label: string; status: string | null; statKey: keyof ScanStats }[] = [
  { label: 'All', status: null, statKey: 'total' },
  { label: 'Succeeded', status: 'succeeded', statKey: 'succeeded' },
  { label: 'Running', status: 'running', statKey: 'running' },
];

const ScansListPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { applicationId: routeApplicationId } = useParams<{ applicationId: string }>();
  const { currentOrganization } = useOrganization();
  const isReadOnly = currentOrganization?.current_user_role === 'viewer';
  const [batches, setBatches] = useState<ScanBatch[]>([]);
  const [queuedBatches, setQueuedBatches] = useState<ScanBatch[]>([]);
  const [applications, setApplications] = useState<ApplicationResponse[]>([]);
  const [totalBatches, setTotalBatches] = useState(0);
  const [stats, setStats] = useState<ScanStats>({ pending: 0, running: 0, succeeded: 0, failed: 0, total: 0 });
  const [hasNext, setHasNext] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const api = useAuthenticatedApi();
  const initialLoadDone = useRef(false);

  // Application + optional filters come from the route param / query string (deep links).
  const applicationIdFilter = routeApplicationId || searchParams.get('application_id');
  const scanTypeFilter = searchParams.get('scan_type');
  const statusFilter = searchParams.get('status');
  const branchFilter = searchParams.get('branch') || '';
  const dateFilter = searchParams.get('date');
  const page = parseInt(searchParams.get('page') || '1', 10);
  const limit = 20;

  const { branches } = useScanBranches(applicationIdFilter || undefined);

  const fetchScans = useCallback(async (showLoader = true) => {
    if (showLoader) setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (applicationIdFilter) params.append('application_id', applicationIdFilter);
      if (scanTypeFilter) params.append('scan_type', scanTypeFilter);
      if (statusFilter) params.append('status', statusFilter);
      if (branchFilter) params.append('branch', branchFilter);
      if (dateFilter) params.append('date', dateFilter);
      const skip = (page - 1) * limit;
      params.append('limit', limit.toString());
      params.append('skip', skip.toString());

      const response = await api.get<ScanListResponse>(`${apiEndpoints.scans.list()}?${params.toString()}`);
      setBatches(response.batches);
      setQueuedBatches(response.queued_batches || []);
      setTotalBatches(response.total_batches);
      setStats(response.stats);
      setHasNext(response.has_next);
    } catch (error) {
      console.error('Error fetching scans:', error);
      toast.error('Failed to load scans');
    } finally {
      if (showLoader) setIsLoading(false);
    }
  }, [api, applicationIdFilter, scanTypeFilter, statusFilter, branchFilter, dateFilter, page, limit]);

  // Immediate refresh when arriving from scan creation.
  useEffect(() => {
    const state = location.state as { newBatchId?: string; timestamp?: number } | null;
    if (state?.newBatchId) {
      fetchScans(false);
      navigate(location.pathname + location.search, { replace: true, state: null });
    }
  }, [location.state, navigate, location.pathname, location.search, fetchScans]);

  // Fetch applications for the name lookup (include archived).
  useEffect(() => {
    const fetchApplications = async () => {
      if (!currentOrganization?.id) return;
      try {
        const response = await api.get<ApplicationListResponse>(
          `${apiEndpoints.applications.list(currentOrganization.id)}?include_archived=true`,
        );
        setApplications(response.applications);
      } catch (error) {
        console.error('Failed to fetch applications:', error);
      }
    };
    fetchApplications();
  }, [currentOrganization?.id, api]);

  // Initial load + silent refresh on filter changes.
  useEffect(() => {
    if (!initialLoadDone.current) {
      initialLoadDone.current = true;
      fetchScans(true);
    } else {
      fetchScans(false);
    }
  }, [fetchScans]);

  // Poll for updates every 3 seconds.
  useEffect(() => {
    const interval = setInterval(() => fetchScans(false), 3000);
    return () => clearInterval(interval);
  }, [fetchScans]);

  const goToPage = (nextPage: number) => {
    const newParams = new URLSearchParams(searchParams);
    if (nextPage <= 1) {
      newParams.delete('page');
    } else {
      newParams.set('page', nextPage.toString());
    }
    setSearchParams(newParams);
  };

  const setBranch = (branch: string) => {
    const newParams = new URLSearchParams(searchParams);
    if (branch) {
      newParams.set('branch', branch);
    } else {
      newParams.delete('branch');
    }
    newParams.delete('page'); // reset pagination when the filter changes
    setSearchParams(newParams);
  };

  const activeStatus = statusFilter || 'all';

  const setStatus = (status: string | null) => {
    const newParams = new URLSearchParams(searchParams);
    if (status) {
      newParams.set('status', status);
    } else {
      newParams.delete('status'); // "All" is the default
    }
    newParams.delete('page'); // reset pagination when the filter changes
    setSearchParams(newParams);
  };

  const branchOptions = [
    { value: '', label: 'All branches' },
    ...branches.map((b) => ({ value: b, label: b })),
  ];

  const applicationNameMap = useMemo(
    () =>
      applications.reduce(
        (acc, app) => {
          acc[app.id] = app.name;
          return acc;
        },
        {} as Record<string, string>,
      ),
    [applications],
  );

  const fromApplication = useMemo<FromApplicationContext>(() => {
    const appName = applicationNameMap[routeApplicationId!] || 'Repository';
    return { appId: routeApplicationId!, appName };
  }, [routeApplicationId, applicationNameMap]);

  // "Running" shows the active (in-progress) scans; "Succeeded" shows the finished list;
  // "All" shows both.
  const showQueued = activeStatus === 'all' || activeStatus === 'running';
  const showCompleted = activeStatus === 'all' || activeStatus === 'succeeded';
  const visibleQueued = showQueued ? queuedBatches : [];
  const visibleCompleted = showCompleted ? batches : [];
  const isEmpty = visibleQueued.length === 0 && visibleCompleted.length === 0;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spinner size={32} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ui-background-primary">
      <div className="mx-auto w-full max-w-screen-2xl px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 mb-7">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-[10px] bg-ui-background-secondary border border-ui-border-primary flex items-center justify-center flex-shrink-0">
              <ScanSearch className="w-[18px] h-[18px] text-brand-primary" />
            </div>
            <h1 className="text-2xl font-bold tracking-tight text-ui-text-primary">Security Scans</h1>
          </div>
          <button
            onClick={() => setIsCreateModalOpen(true)}
            disabled={isReadOnly}
            className="flex items-center gap-2 px-4 py-2.5 bg-brand-primary hover:bg-brand-primaryDark text-black font-semibold rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-brand-primary flex-shrink-0"
          >
            <Plus className="w-4 h-4" />
            New scan
          </button>
        </div>

        {/* Status chips + branch filter */}
        <div className="flex items-start justify-between gap-3 flex-wrap mb-5">
          <div className="flex items-center gap-2 flex-wrap">
            {STATUS_CHIPS.map((chip) => {
              const key = chip.status ?? 'all';
              const active = key === activeStatus;
              return (
                <button
                  key={key}
                  onClick={() => setStatus(chip.status)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    active
                      ? 'bg-brand-primary text-black'
                      : 'bg-ui-background-tertiary text-ui-text-secondary hover:text-ui-text-primary'
                  }`}
                >
                  {chip.label}
                  <span className={active ? 'text-black/60' : 'text-ui-text-tertiary'}>{stats[chip.statKey]}</span>
                </button>
              );
            })}
          </div>
          {branches.length > 0 && (
            <SingleSelectDropdown
              label="All branches"
              ariaLabel="Filter by branch"
              options={branchOptions}
              value={branchFilter}
              onChange={setBranch}
            />
          )}
        </div>

        {/* Running scans lead the table, completed scans follow */}
        {!isEmpty && (
          <div className="border border-ui-border-primary rounded-xl bg-ui-background-secondary overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[44rem]">
                <thead>
                  <tr className="border-b border-ui-border-primary">
                    <th className="text-left text-xs font-medium text-ui-text-secondary uppercase tracking-wider py-3 px-4">
                      Scan
                    </th>
                    <th className="text-left text-xs font-medium text-ui-text-secondary uppercase tracking-wider py-3 px-4">
                      Findings
                    </th>
                    <th className="w-px whitespace-nowrap text-left text-xs font-medium text-ui-text-secondary uppercase tracking-wider py-3 px-4">
                      Branch
                    </th>
                    <th className="w-px whitespace-nowrap text-left text-xs font-medium text-ui-text-secondary uppercase tracking-wider py-3 px-4">
                      Started
                    </th>
                    <th className="w-px py-3 px-4">
                      <span className="sr-only">Review</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-ui-border-primary">
                  {visibleQueued.map((batch) => (
                    <ScanBatchRunningRow
                      key={batch.id}
                      batch={batch}
                      applicationName={applicationNameMap[batch.application_id] || 'Unknown'}
                      showApplicationName={!applicationIdFilter}
                    />
                  ))}
                  {visibleCompleted.map((batch) => (
                    <ScanBatchRow
                      key={batch.id}
                      batch={batch}
                      applicationName={applicationNameMap[batch.application_id] || 'Unknown'}
                      fromApplication={fromApplication}
                      showApplicationName={!applicationIdFilter}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Empty state */}
        {isEmpty && (
          <div className="text-center py-16 border border-dashed border-ui-border-primary rounded-2xl">
            <ScanSearch className="w-9 h-9 text-ui-text-tertiary mx-auto mb-3" />
            <div className="text-[15px] font-semibold text-ui-text-secondary">
              {activeStatus === 'all' ? 'No scans yet' : 'No scans match this filter'}
            </div>
            <p className="text-sm text-ui-text-tertiary mt-1.5">
              {activeStatus === 'all'
                ? 'Run your first scan to surface findings.'
                : 'Try a different filter above.'}
            </p>
          </div>
        )}

        {/* Pagination */}
        {totalBatches > limit && (
          <div className="mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="text-sm text-ui-text-secondary">
              Showing {Math.min((page - 1) * limit + 1, totalBatches)} – {Math.min(page * limit, totalBatches)} of{' '}
              {totalBatches}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={() => goToPage(page - 1)}
                disabled={page === 1}
                className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                  page === 1
                    ? 'bg-ui-background-tertiary text-ui-text-tertiary cursor-not-allowed'
                    : 'bg-brand-primary hover:bg-brand-primaryDark text-black'
                }`}
              >
                Previous
              </button>
              <span className="text-sm text-ui-text-secondary px-2">
                Page {page} of {Math.ceil(totalBatches / limit)}
              </span>
              <button
                onClick={() => goToPage(page + 1)}
                disabled={!hasNext}
                className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                  !hasNext
                    ? 'bg-ui-background-tertiary text-ui-text-tertiary cursor-not-allowed'
                    : 'bg-brand-primary hover:bg-brand-primaryDark text-black'
                }`}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Create Scan Modal */}
      <CreateScanModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        preselectedApplicationId={applicationIdFilter || undefined}
      />
    </div>
  );
};

export default ScansListPage;
